import { Badge, Group, Text, Tooltip } from '@mantine/core'
import { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getGroupLabel } from '@/components/deployments/deployment-list/DeploymentTree/deploymentTreeHelpers'
import { DeploymentStatus } from '@/types/deployment'
import { DeploymentGroup } from '@/types/deploymentGroup'
import { DeviceMetricChart } from './DeviceMetricChart'
import {
  TimeRangeConfig,
  formatMegahertz,
  formatWatts
} from './metricTimeRanges'

const STATUS_COLORS: Partial<Record<DeploymentStatus, string>> = {
  [DeploymentStatus.Running]: 'green',
  [DeploymentStatus.Degraded]: 'orange',
  [DeploymentStatus.Failed]: 'red'
}

type GpuGroupsListProps = {
  gpuIndex: number
  groups?: DeploymentGroup[]
}

export const GpuGroupsList = ({ gpuIndex, groups }: GpuGroupsListProps) => {
  if (!groups) {
    return null
  }

  const onGpu = groups.filter((group) => group.gpu?.index === gpuIndex)

  return (
    <Group gap="xs">
      <Text size="xs" c="dimmed">
        <FormattedMessage id="deployments.details.observability.gpu.groups" />
      </Text>

      {onGpu.length === 0 && (
        <Text size="xs" c="dimmed">
          <FormattedMessage id="deployments.details.observability.gpu.groups.none" />
        </Text>
      )}

      {onGpu.map((group) => (
        <Tooltip key={group.id} label={group.status_display}>
          <Badge
            variant="light"
            color={STATUS_COLORS[group.status] ?? 'gray'}
            size="sm"
          >
            <FormattedMessage
              id="deployments.details.observability.gpu.groups.item"
              values={{
                app: group.application_name,
                number: getGroupLabel(group, groups)
              }}
            />
          </Badge>
        </Tooltip>
      ))}
    </Group>
  )
}

type GpuLoadChartsProps = {
  deviceId: string
  gpu: { index: number; name: string | null }
  groups?: DeploymentGroup[]
  range: TimeRangeConfig
  startTs: number
  endTs: number
  badge?: ReactNode
}

export const GpuLoadCharts = ({
  deviceId,
  gpu,
  groups,
  range,
  startTs,
  endTs,
  badge
}: GpuLoadChartsProps) => {
  const intl = useIntl()
  const footer = <GpuGroupsList gpuIndex={gpu.index} groups={groups} />

  return (
    <>
      <DeviceMetricChart
        deviceId={deviceId}
        metricKey={`gpu_${gpu.index}_power_w`}
        referenceKey={`gpu_${gpu.index}_power_limit_w`}
        referenceLabel={intl.formatMessage({
          id: 'deployments.details.observability.gpuPower.limit'
        })}
        title={
          <FormattedMessage
            id="deployments.details.observability.gpuPower.title"
            values={{ gpuIndex: gpu.index, gpuName: <span>{gpu.name}</span> }}
          />
        }
        badge={badge}
        footer={footer}
        valueFormatter={formatWatts}
        tickFormatter={range.tickFormatter}
        startTs={startTs}
        endTs={endTs}
        agg={range.agg}
        interval={range.interval}
      />

      <DeviceMetricChart
        deviceId={deviceId}
        metricKey={`gpu_${gpu.index}_sm_clock_mhz`}
        title={
          <FormattedMessage
            id="deployments.details.observability.gpuClock.title"
            values={{ gpuIndex: gpu.index, gpuName: <span>{gpu.name}</span> }}
          />
        }
        badge={badge}
        footer={footer}
        valueFormatter={formatMegahertz}
        tickFormatter={range.tickFormatter}
        startTs={startTs}
        endTs={endTs}
        agg={range.agg}
        interval={range.interval}
      />
    </>
  )
}
